import axios from 'axios';

import { env } from 'config';
import {
  OrderColorCode,
  OrderInternalStatusDetails,
  OrdersTab,
  OrderStatusReason,
  UserRole,
} from 'enums';
import {
  PayinOrder,
  PayinOrderDispute,
  ResolvePayinOrderDispute,
  PaginatedData,
  QueryParams,
  CreateOrderDto,
  ExportOrdersPayload,
  OrderReceipt,
  OrderStatusHistory,
  Order,
  Sorting,
} from 'types';
import { apiUtils } from 'utils';

const url = `${env.apiUrl}/orders`;
const exportUrl = `${env.dataApiUrl}/orders`;

const getAllPaginatedAsRole =
  (role: UserRole, tab: OrdersTab) =>
  async (params: QueryParams): Promise<PaginatedData<PayinOrder>> =>
    (await apiUtils.getWithQueryParams(`${url}/${role}/${tab}`, params)).data;

const getOrderExistsByRequisitesAsRole =
  (role: UserRole) =>
  async (requisitesId: string): Promise<{ exists: boolean }> =>
    (await axios.get(`${url}/${role}/exists/requisites/${requisitesId}`)).data;

const confirm = async (order: Partial<PayinOrder>): Promise<PayinOrder> =>
  (await axios.post(`${url}/trader/${order.id}/confirm`, order)).data;

const disputeAsRole =
  (role: UserRole) =>
  async ({
    id,
    data,
  }: {
    id: string;
    data: PayinOrderDispute;
  }): Promise<PayinOrder> =>
    (await axios.post(`${url}/${role}/${id}/dispute`, data)).data;

const resolveDisputeAsRole =
  (role: UserRole) =>
  async ({
    id,
    data,
  }: {
    id: string;
    data: ResolvePayinOrderDispute;
  }): Promise<PayinOrder> =>
    (await axios.post(`${url}/${role}/${id}/resolve-dispute`, data)).data;

const cancelCompletedAsRole =
  (role: UserRole) =>
  async (id: string): Promise<PayinOrder> =>
    (await axios.post(`${url}/${role}/${id}/cancel-completed`)).data;

const cancelledToDisputeAsRole =
  (role: UserRole) =>
  async ({
    id,
    statusReason,
  }: {
    id: string;
    statusReason: OrderStatusReason;
  }): Promise<PayinOrder> =>
    (
      await axios.post(`${url}/${role}/${id}/cancelled-to-dispute`, {
        statusReason,
      })
    ).data;

const updateColorCodeAsRole =
  (role: UserRole) =>
  async ({
    id,
    colorCode,
  }: {
    id: string;
    colorCode: OrderColorCode;
  }): Promise<Order> =>
    (await axios.post(`${url}/${role}/${id}/color-code`, { colorCode })).data;

const updateInternalStatusDetailsAsRole =
  (role: UserRole) =>
  async ({
    id,
    data,
  }: {
    id: string;
    data: { internalStatusDetails: OrderInternalStatusDetails };
  }): Promise<PayinOrder> =>
    (await axios.post(`${url}/${role}/${id}/internal-status-details`, data))
      .data;

const createAsRole =
  (role: UserRole) =>
  async (order: CreateOrderDto): Promise<PayinOrder> =>
    (await axios.post(`${url}/${role}`, order)).data;

const createRequisitesVerificationOrder = async (
  requisitesId: string,
): Promise<PayinOrder> =>
  (
    await axios.post(`${url}/trader/requisites-verification-order`, {
      requisitesId,
    })
  ).data;

const getOneAsTrader = async (id: string): Promise<PayinOrder> =>
  (await axios.get(`${url}/trader/${id}`)).data;

const getOrderStatusHistoryAsRole =
  (role: UserRole) =>
  async (orderId: string): Promise<OrderStatusHistory[]> =>
    (await axios.get(`${url}/${role}/${orderId}/status-history`)).data;

const getOrderReceiptsAsRole =
  (role: UserRole) =>
  async (orderId: string): Promise<OrderReceipt[]> =>
    (await axios.get(`${url}/${role}/${orderId}/receipts`)).data;

const generateReceiptUrlAsRole =
  (role: UserRole) =>
  async ({ orderId, body }: { orderId: string; body: OrderReceipt }) =>
    (await axios.post(`${url}/${role}/${orderId}/receipts/url`, body)).data;

const exportAsRole =
  (role: UserRole) =>
  async ({
    data,
    filters,
    sorting,
  }: {
    data: ExportOrdersPayload;
    filters: string;
    sorting: Sorting;
  }) => {
    const response = await axios.post(`${exportUrl}/${role}/export`, data, {
      params: { filters, ...sorting },
    });
    const url = response?.data?.url;
    if (url) {
      apiUtils.downloadFile(url);
    }
  };

const getExportColumnsAsRole =
  (role: UserRole) => async (): Promise<{ columns: string[] }> =>
    (await axios.get(`${exportUrl}/${role}/export/columns`)).data;

export const ordersApi = {
  getAllPaginatedAsRole,
  getOrderExistsByRequisitesAsRole,
  generateReceiptUrlAsRole,
  getOrderReceiptsAsRole,
  confirm,
  disputeAsRole,
  resolveDisputeAsRole,
  cancelledToDisputeAsRole,
  createAsRole,
  createRequisitesVerificationOrder,
  cancelCompletedAsRole,
  getOneAsTrader,
  exportAsRole,
  getExportColumnsAsRole,
  getOrderStatusHistoryAsRole,
  updateColorCodeAsRole,
  updateInternalStatusDetailsAsRole,
};
